(() => {
  "use strict";

  console.log("[DE] drift-events.js loaded (v3.3 - PRO-AM orgs-only + paged + dedupe + map + debug)");

  // ---------- helpers ----------
  const escapeHtml = (str) =>
    String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  // Robust boolean parsing (handles true/false, "true"/"false", 1/0, "yes"/"no")
  const toBool = (v) => {
    if (v === true) return true;
    if (v === false) return false;
    const s = String(v ?? "").trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes" || s === "y";
  };

  const normalizeHttpUrl = (url) => {
    const s = String(url || "").trim();
    if (!s) return "";
    if (s.startsWith("//")) return "https:" + s;
    return s;
  };

  const openExternal = (url) => {
    const u = normalizeHttpUrl(url);
    if (!u) return;
    window.open(u, "_blank", "noopener,noreferrer");
  };

  const parseDate = (yyyyMmDd) => {
    const s = String(yyyyMmDd || "").trim();
    if (!s) return null;
    const d = new Date(s + "T00:00:00");
    return Number.isFinite(d.getTime()) ? d : null;
  };

  const parseCoords = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return { lat: NaN, lng: NaN };
    const comma = s.split(",").map((x) => x.trim());
    if (comma.length >= 2) return { lat: Number(comma[0]), lng: Number(comma[1]) };
    const ws = s.split(/\s+/);
    if (ws.length >= 2) return { lat: Number(ws[0]), lng: Number(ws[1]) };
    return { lat: NaN, lng: NaN };
  };

  const extractState = (address) => {
    const s = String(address || "").toUpperCase();
    const m = s.match(/,\s*([A-Z]{2})\s*\d{5}(-\d{4})?\b/);
    if (m) return m[1];
    const m2 = s.match(/,\s*([A-Z]{2})\b/);
    return m2 ? m2[1] : "";
  };

  const qsAny = (root, selectors) => {
    for (const s of selectors) {
      const el = root.querySelector(s);
      if (el) return el;
    }
    return null;
  };

  // Carry preview/editor params to JSON endpoints
  const copyPreviewParams = (toUrl) => {
    const cur = new URL(window.location.href);
    const allow = new Set(["preview_theme_id", "theme_editor_preview", "section_id", "oseid", "pb", "_fd"]);
    cur.searchParams.forEach((val, key) => {
      if (key.startsWith("preview_") || allow.has(key)) {
        if (!toUrl.searchParams.has(key)) toUrl.searchParams.set(key, val);
      }
    });
  };

  async function fetchJsonBOMSafe(url) {
    const res = await fetch(url, { cache: "no-store", credentials: "same-origin" });
    const txt = (await res.text()).replace(/^\uFEFF/, "").trim();
    return JSON.parse(txt);
  }

  async function fetchPaged(urlBase, maxPagesCap) {
    const firstUrl = new URL(urlBase, window.location.origin);
    firstUrl.searchParams.set("page", "1");
    firstUrl.searchParams.set("_ts", String(Date.now()));
    copyPreviewParams(firstUrl);

    console.log("[DE] Fetch page 1:", firstUrl.toString());
    const first = await fetchJsonBOMSafe(firstUrl.toString());

    const pages = Math.max(1, Number(first.pages || 1));
    const key = Array.isArray(first.events) ? "events" : Array.isArray(first.organizers) ? "organizers" : null;
    if (!key) throw new Error(`Unknown JSON payload shape from ${urlBase}`);

    const pageParam = (first.page_param && String(first.page_param).trim()) || "page";
    console.log("[DE] Using pageParam:", pageParam);

    const want = Math.min(pages, Math.max(1, Number(maxPagesCap || 1)));

    const out = [];
    out.push(...(first[key] || []));

    for (let p = 2; p <= want; p++) {
      const u = new URL(urlBase, window.location.origin);

      u.searchParams.set(pageParam, String(p));
      if (pageParam !== "page") u.searchParams.delete("page");

      u.searchParams.set("_ts", String(Date.now()));
      copyPreviewParams(u);

      console.log(`[DE] Fetch page ${p}:`, u.toString());
      const j = await fetchJsonBOMSafe(u.toString());
      out.push(...(j[key] || []));
    }

    return { pages, items: out, page_param: pageParam };
  }

  function dedupeByHandle(items, label) {
    const map = new Map();
    let missing = 0;
    for (const it of items || []) {
      const h = it && it.handle != null ? String(it.handle).trim() : "";
      if (!h) {
        missing += 1;
        continue;
      }
      if (!map.has(h)) map.set(h, it);
    }
    if (missing) console.warn(`[DE] ${label}: dropped ${missing} items missing handle`);
    return Array.from(map.values());
  }

  // ---------- root/config ----------
  const root = document.querySelector(".drift-events");
  if (!root) return;

  // Debug mode enabled ONLY when URL includes ?de_debug=1
  const DE_DEBUG = new URLSearchParams(window.location.search).get("de_debug") === "1";

  const eventsUrlBase = (root.dataset.eventsUrl || "").trim();
  const organizersUrlBase = (root.dataset.organizersUrl || "").trim();
  const googleKey = (root.dataset.googleMapsKey || "").trim();

  console.log("[DE] eventsUrl:", eventsUrlBase);
  console.log("[DE] organizersUrl:", organizersUrlBase);

  // Per-page UI size (cards + pins)
  const pageSize = Math.max(1, Number(root.dataset.maxUpcoming || 30));

  // Fetch cap from section setting
  const MAX_FETCH_PAGES = Math.min(50, Math.max(1, Number(root.dataset.maxPages || 10)));

  // ---------- DOM ----------
  const $cards = document.getElementById("de-cards");
  const $mapEl = document.getElementById("de-map");
  const $countLoadedEls = Array.from(document.querySelectorAll("#de-count-loaded, [data-de-count-loaded]"));
  const $countShowingEls = Array.from(document.querySelectorAll("#de-count-showing, [data-de-count-showing]"));
  const $status = document.getElementById("de-status");

  const $search = qsAny(root, ["[data-de-search]", "#de-search"]);
  const $state = qsAny(root, ["[data-de-state]", "#de-state"]);
  const $proam = qsAny(root, ["[data-de-proam]", "#de-proam"]);
  const $from = qsAny(root, ["[data-de-date-from]"]);
  const $to = qsAny(root, ["[data-de-date-to]"]);
  const $tabs = Array.from(root.querySelectorAll("[data-de-tab]"));

  const $orgMs = root.querySelector("[data-de-org-ms]");
  const $orgToggle = root.querySelector("[data-de-org-toggle]");
  const $orgPanel = root.querySelector("[data-de-org-panel]");
  const $orgList = root.querySelector("[data-de-org-list]");
  const $orgAll = root.querySelector("[data-de-org-all]");
  const $orgNone = root.querySelector("[data-de-org-none]");

  const $pager = root.querySelector("[data-de-pager]");
  const $pagerPrev = $pager?.querySelector("[data-de-pager-prev]");
  const $pagerNext = $pager?.querySelector("[data-de-pager-next]");
  const $pagerLabel = $pager?.querySelector("[data-de-pager-label]");

  const $openSubmit = root.querySelector("[data-de-open-submit]");
  const $modal = root.querySelector("[data-de-modal]");
  const $modalCloses = Array.from(root.querySelectorAll("[data-de-close]"));

  const $submitOrgSelect = root.querySelector("[data-de-submit-org-select]");
  const $submitOrgHandle = root.querySelector("[data-de-submit-org-handle]");
  const $submitOrgOther = root.querySelector("[data-de-submit-org-other]");
  const $submitForm = root.querySelector(".de-form");

  if (!$cards) return;

  function setStatus(msg) {
    if ($status) $status.textContent = msg || "";
  }

  function setCounts(loaded, showing) {
    $countLoadedEls.forEach((el) => (el.textContent = String(loaded)));
    $countShowingEls.forEach((el) => (el.textContent = String(showing)));
  }

  // ---------- state ----------
  let organizers = [];
  let organizerByHandle = new Map();

  let allEvents = [];
  let filteredCache = [];

  let selectedOrgHandles = new Set();
  let activeTab = "all";
  let pageIndex = 0;

  let selectedHandle = "";
  const MAP_FOCUS_ZOOM = 12;

  let __cardsDelegatedClickWired = false;

  // ---------- DEBUG REPORT ----------
  let __DE_DEBUG_ROWS = [];

  function deCsvEscape(v) {
    const s = String(v ?? "");
    return `"${s.replace(/"/g, '""')}"`;
  }

  function deDownloadCsv(rows, filename) {
    const headers = [
      "status",
      "reason",
      "handle",
      "name",
      "venue",
      "address",
      "state",
      "start_date",
      "end_date",
      "organizer",
      "coords",
      "coords_status",
      "map_pin_this_page",
      "map_reason",
      "admin_hint",
    ];

    const lines = [
      headers.join(","),
      ...rows.map((r) =>
        [
          deCsvEscape(r.status),
          deCsvEscape(r.reason),
          deCsvEscape(r.handle),
          deCsvEscape(r.name),
          deCsvEscape(r.venue),
          deCsvEscape(r.address),
          deCsvEscape(r.state),
          deCsvEscape(r.start_date),
          deCsvEscape(r.end_date),
          deCsvEscape(r.organizer_name),
          deCsvEscape(r.coords),
          deCsvEscape(r.coords_status),
          deCsvEscape(r.map_pin_this_page ? "YES" : "NO"),
          deCsvEscape(r.map_reason),
          deCsvEscape(r.admin_hint),
        ].join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }

  function deEnsurePanel() {
    if (!DE_DEBUG) return null;

    let panel = document.getElementById("de-debug-panel");
    if (panel) return panel;

    panel = document.createElement("div");
    panel.id = "de-debug-panel";
    panel.style.cssText = [
      "position:fixed",
      "right:12px",
      "bottom:12px",
      "z-index:99999",
      "background:#111",
      "color:#fff",
      "padding:10px 12px",
      "border-radius:10px",
      "font:12px/1.3 system-ui, -apple-system, Segoe UI, Roboto, Arial",
      "box-shadow:0 10px 30px rgba(0,0,0,.35)",
      "max-width:310px",
    ].join(";");

    panel.innerHTML = `
      <div style="font-weight:800; margin-bottom:6px;">DE Debug</div>
      <div style="opacity:.9; margin-bottom:8px;">
        <div>Total fetched: <b data-deb-total>0</b></div>
        <div><span style="color:#7CFC00;">●</span> Showing: <b data-deb-showing>0</b></div>
        <div><span style="color:#FF4D4D;">●</span> Past removed: <b data-deb-past>0</b></div>
        <div>No-date kept: <b data-deb-nodate>0</b></div>
        <div style="margin-top:6px;">
          <div>Map pins (this page): <b data-deb-pins>0</b></div>
          <div>Coords missing: <b data-deb-cmiss>0</b></div>
          <div>Coords invalid: <b data-deb-cbad>0</b></div>
        </div>
      </div>
      <div style="display:flex; gap:8px; flex-wrap:wrap;">
        <button type="button" data-deb-csv style="cursor:pointer; padding:6px 8px; border-radius:8px; border:0; font-weight:700;">Download CSV</button>
        <button type="button" data-deb-console style="cursor:pointer; padding:6px 8px; border-radius:8px; border:0; font-weight:700;">Console table</button>
      </div>
      <div style="margin-top:8px; opacity:.75;">
        Tip: open with <b>?de_debug=1</b>
      </div>
    `;

    document.body.appendChild(panel);

    panel.querySelector("[data-deb-csv]")?.addEventListener("click", () => {
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      deDownloadCsv(__DE_DEBUG_ROWS, `drift-events-report-${stamp}.csv`);
    });

    panel.querySelector("[data-deb-console]")?.addEventListener("click", () => {
      console.table(__DE_DEBUG_ROWS);
    });

    return panel;
  }

  function deCoordsStatus(e) {
    const raw = String(e?.coords ?? "").trim();
    const lat = e?.lat;
    const lng = e?.lng;

    if (!raw) return { status: "MISSING", reason: "COORDS_EMPTY" };
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { status: "INVALID", reason: "COORDS_NOT_PARSEABLE" };
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return { status: "INVALID", reason: "COORDS_OUT_OF_RANGE" };

    return { status: "OK", reason: "OK" };
  }

  function deBuildReportRows(all, today, ctx) {
    const q = (ctx.q || "").trim().toLowerCase();
    const st = String(ctx.st || "").trim();
    const selectedSet = ctx.selectedOrgHandles || new Set();
    const active = String(ctx.activeTab || "all");
    const bms = ctx.bookmarksSet || new Set();
    const from = ctx.from || null;
    const to = ctx.to || null;
    const shownSet = ctx.shownSet || new Set();

    return (all || []).map((e) => {
      const end = e._endDate || e._startDate;

      let status = "SHOWING";
      let reason = "OK";

      if (end && end < today) {
        status = "PAST";
        reason = "ENDED_BEFORE_TODAY";
      } else if (active === "bookmarks" && !bms.has(String(e.handle))) {
        status = "HIDDEN";
        reason = "NOT_BOOKMARKED";
      } else if (q) {
        const hay = `${e.name} ${e.venue} ${e.address} ${e.organizer_name} ${e._state}`.toLowerCase();
        if (!hay.includes(q)) {
          status = "HIDDEN";
          reason = "SEARCH_FILTER";
        }
      } else if (selectedSet && selectedSet.size > 0 && !selectedSet.has(e.organizer_handle)) {
        status = "HIDDEN";
        reason = "ORGANIZER_FILTER";
      } else if (st && e._state !== st) {
        status = "HIDDEN";
        reason = "STATE_FILTER";
      } else if (from || to) {
        const s = e._startDate || e._endDate;
        const en = e._endDate || e._startDate;
        if (s && en) {
          if (from && en < from) {
            status = "HIDDEN";
            reason = "DATE_RANGE_BEFORE";
          } else if (to && s > to) {
            status = "HIDDEN";
            reason = "DATE_RANGE_AFTER";
          }
        }
      }

      const cs = deCoordsStatus(e);
      const coords_status = cs.status;
      let map_pin_this_page = false;
      let map_reason = "OK";

      if (status !== "SHOWING") {
        map_reason = `NOT_SHOWING_${status}_${reason}`;
      } else if (coords_status !== "OK") {
        map_reason = cs.reason;
      } else if (!shownSet.has(String(e.handle))) {
        map_reason = "NOT_ON_CURRENT_PAGER_PAGE";
      } else {
        map_pin_this_page = true;
        map_reason = "PINNED";
      }

      return {
        status,
        reason,
        handle: e.handle,
        name: e.name,
        venue: e.venue,
        address: e.address,
        state: e._state,
        start_date: e.start_date,
        end_date: e.end_date,
        organizer_name: e.organizer_name,
        coords: e.coords,
        coords_status,
        map_pin_this_page,
        map_reason,
        admin_hint: `Shopify Admin → Content → Metaobjects → drift_events → search handle: ${e.handle}`,
      };
    });
  }

  function deUpdateDebugPanel(shownList) {
    if (!DE_DEBUG) return;

    const panel = deEnsurePanel();
    if (!panel) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = ($search?.value || "").trim().toLowerCase();
    const st = ($state?.value || "").trim();
    const from = $from?.value ? new Date(`${$from.value}T00:00:00`) : null;
    const to = $to?.value ? new Date(`${$to.value}T23:59:59`) : null;

    const bookmarksSet = getBookmarks();
    const shownSet = new Set((shownList || []).map((x) => String(x.handle)));

    __DE_DEBUG_ROWS = deBuildReportRows(allEvents, today, {
      q,
      st,
      from,
      to,
      selectedOrgHandles,
      activeTab,
      bookmarksSet,
      shownSet,
    });

    const total = __DE_DEBUG_ROWS.length;
    const past = __DE_DEBUG_ROWS.filter((r) => r.status === "PAST").length;
    const nodate = allEvents.filter((e) => !(e._endDate || e._startDate)).length;

    const showing = __DE_DEBUG_ROWS.filter((r) => r.status === "SHOWING").length;

    const pins = __DE_DEBUG_ROWS.filter((r) => r.map_pin_this_page).length;
    const cmiss = __DE_DEBUG_ROWS.filter((r) => r.coords_status === "MISSING").length;
    const cbad = __DE_DEBUG_ROWS.filter((r) => r.coords_status === "INVALID").length;

    panel.querySelector("[data-deb-total]").textContent = String(total);
    panel.querySelector("[data-deb-showing]").textContent = String(showing);
    panel.querySelector("[data-deb-past]").textContent = String(past);
    panel.querySelector("[data-deb-nodate]").textContent = String(nodate);

    panel.querySelector("[data-deb-pins]").textContent = String(pins);
    panel.querySelector("[data-deb-cmiss]").textContent = String(cmiss);
    panel.querySelector("[data-deb-cbad]").textContent = String(cbad);
  }

  // ---------- bookmarks ----------
  const LS_KEY = "de_bookmarks_guest_v1";
  const MIGRATE_KEY = "de_bookmarks_migrated_v1";
  const BOOKMARKS_ENDPOINT = "/apps/ghostline/bookmarks";

  let __bmMode = "local";
  let __bmSet = new Set();

  function loadBookmarksFromLocal() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr.map(String) : []);
    } catch {
      return new Set();
    }
  }

  function saveBookmarksToLocal() {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(Array.from(__bmSet)));
    } catch {}
  }

  async function loadBookmarksFromServer() {
    const r = await fetch(BOOKMARKS_ENDPOINT, { method: "GET", credentials: "same-origin" });
    if (r.status === 401) return null;
    if (!r.ok) throw new Error(`Bookmarks GET failed: ${r.status}`);
    const data = await r.json();
    return Array.isArray(data.bookmarks) ? data.bookmarks.map(String) : [];
  }

  async function saveBookmarkToServer(eventId, action) {
    const r = await fetch(BOOKMARKS_ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventId: String(eventId), action }),
    });
    if (r.status === 401) return null;
    if (!r.ok) throw new Error(`Bookmarks POST failed: ${r.status}`);
    const data = await r.json();
    return Array.isArray(data.bookmarks) ? data.bookmarks.map(String) : [];
  }

  async function initBookmarks() {
    try {
      const server = await loadBookmarksFromServer();
      if (server !== null) {
        __bmMode = "server";

        const serverSet = new Set(server.map(String));
        const alreadyMigrated = localStorage.getItem(MIGRATE_KEY) === "1";
        const localSet = loadBookmarksFromLocal();

        if (!alreadyMigrated && localSet.size > 0) {
          for (const id of localSet) {
            if (!serverSet.has(id)) {
              try {
                await saveBookmarkToServer(id, "add");
              } catch (e) {
                console.warn("[DE] migrate add failed", id, e);
              }
            }
          }
          try {
            localStorage.removeItem(LS_KEY);
          } catch {}
          try {
            localStorage.setItem(MIGRATE_KEY, "1");
          } catch {}
        }

        const refreshed = await loadBookmarksFromServer();
        __bmSet = new Set((refreshed !== null ? refreshed : server).map(String));
        return;
      }
    } catch (e) {
      console.warn("[DE] Server bookmarks failed, using localStorage", e);
    }

    __bmMode = "local";
    __bmSet = loadBookmarksFromLocal();
  }

  function getBookmarks() {
    return __bmSet;
  }

  async function toggleBookmark(eventId) {
    const id = String(eventId);
    const willBeOn = !__bmSet.has(id);

    if (willBeOn) __bmSet.add(id);
    else __bmSet.delete(id);

    if (__bmMode === "server") {
      const updated = await saveBookmarkToServer(id, willBeOn ? "add" : "remove");
      if (updated !== null) __bmSet = new Set(updated);
      else {
        __bmMode = "local";
        saveBookmarksToLocal();
      }
    } else {
      saveBookmarksToLocal();
    }
  }

  // ---------- selection ----------
  function setSelectedCard(handle) {
    if (!handle) return;
    $cards.querySelectorAll(".de-card.is-selected").forEach((el) => el.classList.remove("is-selected"));
    const el = $cards.querySelector(`.de-card[data-de-handle="${CSS.escape(handle)}"]`);
    if (!el) return;
    el.classList.add("is-selected");

    const container = $cards;
    const canScroll = container && container.scrollHeight > container.clientHeight + 1;
    if (canScroll) {
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      const padding = 8;
      const targetTop = container.scrollTop + (eRect.top - cRect.top) - padding;
      container.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }

  function selectEvent(handle, source) {
    if (!handle) return;
    selectedHandle = handle;
    setSelectedCard(handle);
    if (source !== "map" && window.__DE_MAP?.focus) window.__DE_MAP.focus(handle);
  }

  // ---------- pager ----------
  function updatePager(totalPages, totalMatches) {
    if (!$pager || !$pagerPrev || !$pagerNext || !$pagerLabel) return;
    if (totalPages <= 1) {
      $pager.hidden = true;
      return;
    }
    $pager.hidden = false;
    $pagerLabel.textContent = `Page ${pageIndex + 1} of ${totalPages} • ${totalMatches} matches`;
    $pagerPrev.disabled = pageIndex <= 0;
    $pagerNext.disabled = pageIndex >= totalPages - 1;
  }

  function getPageSlice(list) {
    const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
    pageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return { totalPages, shown: list.slice(start, end) };
  }

  // ---------- cards ----------
  function cardHTML(e) {
    const bms = getBookmarks();
    const bmOn = bms.has(e.handle);

    const ticket = e.tickets_url
      ? `<a class="de-btn" href="${escapeHtml(normalizeHttpUrl(e.tickets_url))}" target="_blank" rel="noopener noreferrer" data-de-ticket>Tickets</a>`
      : "";

    const logo = e.organizer_logo
      ? `<img class="de-orglogo" src="${escapeHtml(normalizeHttpUrl(e.organizer_logo))}" alt="${escapeHtml(e.organizer_name)}" loading="lazy">`
      : `<div class="de-orglogo--empty" aria-hidden="true"></div>`;

    const dates = e.start_date
      ? `${escapeHtml(e.start_date)}${e.end_date && e.end_date !== e.start_date ? " → " + escapeHtml(e.end_date) : ""}`
      : "";

    const key = String(e.handle || "");

    return `
      <div class="de-card drift-event-card ${selectedHandle === key ? "is-selected" : ""}"
        data-de-handle="${escapeHtml(key)}"
        id="event-${escapeHtml(key)}">
        <button class="de-bookmark ${bmOn ? "is-on" : ""}" type="button"
          title="Bookmark" aria-label="Bookmark" data-de-bm="${escapeHtml(key)}">★</button>

        <div class="de-card__row">
          <div class="de-card__main">
            <div class="de-title">${escapeHtml(e.name || "")}</div>
            ${e.venue ? `<div class="de-meta">${escapeHtml(e.venue)}</div>` : ""}
            ${e.address ? `<div class="de-meta">${escapeHtml(e.address)}</div>` : ""}
            ${dates ? `<div class="de-meta">${dates}</div>` : ""}
            <div class="de-actions">${ticket}</div>
          </div>

          <div class="de-card__org">
            ${logo}
            <div class="de-orgname">${escapeHtml(e.organizer_name || "")}</div>
            ${e.pro_am ? `<div class="de-orgtag">PRO-AM</div>` : ""}
          </div>
        </div>
      </div>
    `;
  }

  function wireCardsDelegatedClickOnce() {
    if (__cardsDelegatedClickWired) return;
    __cardsDelegatedClickWired = true;

    $cards.addEventListener("click", (ev) => {
      const t = ev.target;
      if (!t) return;
      if (t.closest("[data-de-bm]") || t.closest("[data-de-ticket]")) return;
      const card = t.closest(".de-card[data-de-handle]");
      if (!card) return;
      const handle = card.getAttribute("data-de-handle");
      if (!handle) return;
      selectEvent(handle, "card");
    });
  }

  function renderCards(shown) {
    if (!shown.length) {
      $cards.innerHTML = `<div class="de-empty">${activeTab === "bookmarks" ? "No bookmarked events match your filters." : "No events match your filters."}</div>`;
      return;
    }

    $cards.innerHTML = shown.map(cardHTML).join("");
    wireCardsDelegatedClickOnce();

    $cards.querySelectorAll("[data-de-bm]").forEach((btn) => {
      btn.addEventListener("click", async (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const handle = btn.getAttribute("data-de-bm");
        if (!handle) return;
        try {
          await toggleBookmark(handle);
        } catch (e) {
          console.error("[DE] bookmark toggle failed", e);
          alert("Could not save bookmark. Please try again.");
        }
        render();
      });
    });

    $cards.querySelectorAll("[data-de-ticket]").forEach((a) => {
      a.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        openExternal(a.getAttribute("href"));
      });
    });
  }

  // ---------- PRO-AM organizers-only view ----------
  function proAmOrgCardHTML(o) {
    const logo = o.logo
      ? `<img class="de-orglogo" src="${escapeHtml(normalizeHttpUrl(o.logo))}" alt="${escapeHtml(o.name)}" loading="lazy">`
      : `<div class="de-orglogo--empty" aria-hidden="true"></div>`;

    const websiteBtn = o.website
      ? `<button type="button" class="de-btn" data-de-org-website="${escapeHtml(normalizeHttpUrl(o.website))}">Website</button>`
      : "";

    return `
      <div class="de-card de-org-card">
        <div class="de-card__row">
          <div class="de-card__main">
            <div class="de-title">${escapeHtml(o.name)}</div>
            <div class="de-meta" style="opacity:.85;">Organizer</div>
            <div class="de-actions">${websiteBtn}</div>
          </div>
          <div class="de-card__org">
            ${logo}
            <div class="de-orgname">${escapeHtml(o.name)}</div>
            <div class="de-orgtag">PRO-AM</div>
          </div>
        </div>
      </div>
    `;
  }

  function renderProAmOrganizersOnly() {
    const list = (organizers || []).filter((o) => toBool(o.pro_am));

    // counts become "organizers loaded/showing"
    setCounts(list.length, list.length);

    // hide pager always in this mode
    if ($pager) $pager.hidden = true;

    if (!list.length) {
      $cards.innerHTML = `<div class="de-empty">No PRO-AM organizers found.</div>`;
      return;
    }

    $cards.innerHTML = list.map(proAmOrgCardHTML).join("");

    // wire website buttons (no event selection / bookmarks in this mode)
    $cards.querySelectorAll("[data-de-org-website]").forEach((btn) => {
      btn.addEventListener("click", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        openExternal(btn.getAttribute("data-de-org-website"));
      });
    });

    // clear map pins if map is loaded (optional but keeps state clean)
    if (window.__DE_MAP?.update) window.__DE_MAP.update([]);
  }

  // ---------- organizers UI ----------
  function updateOrgButtonLabel() {
    if (!$orgToggle) return;
    if (selectedOrgHandles.size === 0) return ($orgToggle.textContent = "All organizers");
    if (selectedOrgHandles.size === 1) {
      const only = Array.from(selectedOrgHandles)[0];
      $orgToggle.textContent = organizerByHandle.get(only)?.name || "1 organizer";
      return;
    }
    $orgToggle.textContent = `${selectedOrgHandles.size} organizers`;
  }

  function renderOrganizerMultiselect() {
    if (!$orgList) return;

    $orgList.innerHTML = organizers
      .map((o) => {
        const checked = selectedOrgHandles.has(o.handle) ? "checked" : "";
        return `
          <label class="de-ms__item">
            <input type="checkbox" data-de-org-check value="${escapeHtml(o.handle)}" ${checked} />
            <span>${escapeHtml(o.name)}</span>
          </label>
        `;
      })
      .join("");

    $orgList.querySelectorAll("[data-de-org-check]").forEach((cb) => {
      cb.addEventListener("change", () => {
        cb.checked ? selectedOrgHandles.add(cb.value) : selectedOrgHandles.delete(cb.value);
        updateOrgButtonLabel();
        pageIndex = 0;
        render();
      });
    });

    updateOrgButtonLabel();
  }

  function wireOrganizerDropdown() {
    if (!$orgToggle || !$orgPanel) return;

    $orgToggle.addEventListener("click", () => ($orgPanel.hidden = !$orgPanel.hidden));

    document.addEventListener("click", (e) => {
      if (!$orgMs) return;
      if ($orgMs.contains(e.target)) return;
      $orgPanel.hidden = true;
    });

    $orgAll?.addEventListener("click", () => {
      selectedOrgHandles = new Set(organizers.map((o) => o.handle));
      renderOrganizerMultiselect();
      pageIndex = 0;
      render();
    });

    $orgNone?.addEventListener("click", () => {
      selectedOrgHandles.clear();
      renderOrganizerMultiselect();
      pageIndex = 0;
      render();
    });
  }

  // ---------- submit modal organizer select ----------
  function populateSubmitOrganizerSelect() {
    if (!$submitOrgSelect) return;

    const options = [
      `<option value="">Select an organizer</option>`,
      ...organizers.map((o) => `<option value="${escapeHtml(o.handle)}">${escapeHtml(o.name)}</option>`),
    ];

    $submitOrgSelect.innerHTML = options.join("");

    if ($submitOrgSelect.__deWired) return;
    $submitOrgSelect.__deWired = true;

    const sync = () => {
      const handle = String($submitOrgSelect.value || "").trim();
      if ($submitOrgHandle) $submitOrgHandle.value = handle;

      if ($submitOrgOther) {
        if (handle) {
          $submitOrgOther.required = false;
          $submitOrgOther.value = "";
          $submitOrgOther.disabled = true;
        } else {
          $submitOrgOther.disabled = false;
          $submitOrgOther.required = true;
        }
      }
    };

    $submitOrgSelect.addEventListener("change", sync);
    sync();
  }

  function wireSubmitFormValidation() {
    if (!$submitForm || $submitForm.__deWired) return;
    $submitForm.__deWired = true;

    $submitForm.addEventListener("submit", (ev) => {
      const selected = String($submitOrgSelect?.value || "").trim();
      const other = String($submitOrgOther?.value || "").trim();
      if (!selected && !other) {
        ev.preventDefault();
        alert("Please select an organizer or type the organizer name.");
        ($submitOrgSelect || $submitOrgOther)?.focus?.();
      }
    });
  }

  // ---------- filtering ----------
  function applyFilters(events) {
    let out = events.slice();

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    out = out.filter((e) => {
      const end = e._endDate || e._startDate;
      if (!end) return true;
      return end >= today;
    });

    if (activeTab === "bookmarks") {
      const bms = getBookmarks();
      out = out.filter((e) => bms.has(e.handle));
    }

    const q = ($search?.value || "").trim().toLowerCase();
    if (q) {
      out = out.filter((e) => {
        const hay = `${e.name} ${e.venue} ${e.address} ${e.organizer_name} ${e._state}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (selectedOrgHandles.size > 0) out = out.filter((e) => selectedOrgHandles.has(e.organizer_handle));

    const st = ($state?.value || "").trim();
    if (st) out = out.filter((e) => e._state === st);

    const from = $from?.value ? new Date(`${$from.value}T00:00:00`) : null;
    const to = $to?.value ? new Date(`${$to.value}T23:59:59`) : null;
    if (from || to) {
      out = out.filter((e) => {
        const s = e._startDate || e._endDate;
        const en = e._endDate || e._startDate;
        if (!s || !en) return true;
        if (from && en < from) return false;
        if (to && s > to) return false;
        return true;
      });
    }

    out.sort((a, b) => {
      const aa = a._startDate ? a._startDate.getTime() : Number.POSITIVE_INFINITY;
      const bb = b._startDate ? b._startDate.getTime() : Number.POSITIVE_INFINITY;
      return aa - bb;
    });

    return out;
  }

  // ---------- render ----------
  function render() {
    const isProamMode = !!$proam?.checked;
    root.classList.toggle("is-proam-orgs", isProamMode);

    // PRO-AM mode: organizers only (no events/pager/map/debug)
    if (isProamMode) {
      selectedHandle = "";
      pageIndex = 0;
      filteredCache = [];
      renderProAmOrganizersOnly();
      return;
    }

    // Normal events mode
    filteredCache = applyFilters(allEvents);
    const { totalPages, shown } = getPageSlice(filteredCache);

    setCounts(filteredCache.length, shown.length);
    updatePager(totalPages, filteredCache.length);

    renderCards(shown);

    if (window.__DE_MAP?.update) {
      window.__DE_MAP.update(shown);
      if (selectedHandle) window.__DE_MAP.softFocus(selectedHandle);
    }

    deUpdateDebugPanel(shown);
  }

  // ---------- controls ----------
  function wireControls() {
    const rer = () => {
      pageIndex = 0;
      selectedHandle = "";
      render();
    };

    $search?.addEventListener("input", rer);
    $state?.addEventListener("change", rer);
    $proam?.addEventListener("change", rer);
    $from?.addEventListener("change", rer);
    $to?.addEventListener("change", rer);

    $tabs.forEach((btn) => {
      btn.addEventListener("click", () => {
        $tabs.forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");
        activeTab = btn.getAttribute("data-de-tab") || "all";
        pageIndex = 0;
        selectedHandle = "";
        render();
      });
    });

    const $clearFiltersBtn = root.querySelector("[data-de-clear-filters]");

    $pagerPrev?.addEventListener("click", () => {
      if (pageIndex <= 0) return;
      pageIndex -= 1;
      selectedHandle = "";
      render();
    });

    $pagerNext?.addEventListener("click", () => {
      const totalPages = Math.max(1, Math.ceil(filteredCache.length / pageSize));
      if (pageIndex >= totalPages - 1) return;
      pageIndex += 1;
      selectedHandle = "";
      render();
    });

    $clearFiltersBtn?.addEventListener("click", () => {
      if ($search) $search.value = "";
      if ($state) $state.value = "";
      if ($proam) $proam.checked = false;
      if ($from) $from.value = "";
      if ($to) $to.value = "";

      selectedOrgHandles.clear();
      if ($orgList)
        $orgList
          .querySelectorAll('input[type="checkbox"][data-de-org-check]')
          .forEach((cb) => (cb.checked = false));
      updateOrgButtonLabel();
      if ($orgPanel) $orgPanel.hidden = true;

      activeTab = "all";
      $tabs.forEach((b) => b.classList.remove("is-active"));
      const allBtn = $tabs.find((b) => (b.getAttribute("data-de-tab") || "") === "all");
      if (allBtn) allBtn.classList.add("is-active");

      pageIndex = 0;
      selectedHandle = "";
      render();
    });
  }

  // ---------- modal ----------
  function wireModal() {
    $openSubmit?.addEventListener("click", () => {
      if ($modal) $modal.hidden = false;
    });
    $modalCloses.forEach((el) => {
      el.addEventListener("click", () => {
        if ($modal) $modal.hidden = true;
      });
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && $modal && !$modal.hidden) $modal.hidden = true;
    });
  }

  // ---------- map (non-blocking + sync) ----------
  function initMapNonBlocking() {
    if (!$mapEl || !googleKey) {
      console.warn("[DE] Map disabled: missing #de-map or Google key");
      return;
    }

    const loadGoogleMaps = () =>
      new Promise((resolve, reject) => {
        if (window.google?.maps) return resolve();

        const cbName = `__deMapsInit_${Math.random().toString(36).slice(2)}`;
        window[cbName] = () => resolve();

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleKey)}&callback=${encodeURIComponent(cbName)}`;
        script.async = true;
        script.onerror = () => reject(new Error("Failed to load Google Maps"));
        document.head.appendChild(script);
      });

    loadGoogleMaps()
      .then(() => {
        const map = new google.maps.Map($mapEl, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        const infoWindow = new google.maps.InfoWindow();
        let markers = [];
        let markerByHandle = new Map();
        let eventByHandle = new Map();
        let bounceTimer = null;

        const clearMarkers = () => {
          markers.forEach((m) => m.setMap(null));
          markers = [];
          markerByHandle = new Map();
          eventByHandle = new Map();
        };

        const openInfo = (handle) => {
          const e = eventByHandle.get(handle);
          const m = markerByHandle.get(handle);
          if (!e || !m) return;

          const dates = e.start_date
            ? `${escapeHtml(e.start_date)}${e.end_date && e.end_date !== e.start_date ? " → " + escapeHtml(e.end_date) : ""}`
            : "";

          infoWindow.setContent(`
            <div style="font-family:system-ui;font-size:14px;max-width:280px;">
              <div style="font-weight:800;margin-bottom:4px;">${escapeHtml(e.name || "")}</div>
              ${e.venue ? `<div style="opacity:.9;">${escapeHtml(e.venue)}</div>` : ""}
              ${e.address ? `<div style="opacity:.8;margin-top:4px;">${escapeHtml(e.address)}</div>` : ""}
              ${dates ? `<div style="opacity:.8;margin-top:4px;">${dates}</div>` : ""}
              ${
                e.tickets_url
                  ? `<div style="margin-top:10px;"><a href="${escapeHtml(normalizeHttpUrl(e.tickets_url))}" target="_blank" rel="noopener noreferrer">Tickets</a></div>`
                  : ""
              }
            </div>
          `);
          infoWindow.open({ map, anchor: m });
        };

        const bounce = (handle) => {
          const m = markerByHandle.get(handle);
          if (!m) return;

          markers.forEach((x) => x.setAnimation(null));
          if (bounceTimer) clearTimeout(bounceTimer);

          m.setAnimation(google.maps.Animation.BOUNCE);
          bounceTimer = setTimeout(() => m.setAnimation(null), 700);
        };

        const update = (shown) => {
          clearMarkers();

          const bounds = new google.maps.LatLngBounds();
          let any = false;

          shown.forEach((e) => {
            if (!Number.isFinite(e.lat) || !Number.isFinite(e.lng)) return;

            any = true;
            eventByHandle.set(e.handle, e);

            const pos = { lat: e.lat, lng: e.lng };
            const m = new google.maps.Marker({ map, position: pos, title: e.name || "" });

            markerByHandle.set(e.handle, m);
            markers.push(m);
            bounds.extend(pos);

            m.addListener("click", () => {
              focus(e.handle);
              selectEvent(e.handle, "map");
            });
          });

          if (any) {
            map.fitBounds(bounds);
            if (shown.length === 1) map.setZoom(12);
          }
        };

        const focus = (handle) => {
          const m = markerByHandle.get(handle);
          if (!m) return;

          const pos = m.getPosition();
          if (pos) map.panTo(pos);

          const currentZoom = map.getZoom();
          if (typeof currentZoom === "number" && currentZoom < MAP_FOCUS_ZOOM) map.setZoom(MAP_FOCUS_ZOOM);

          openInfo(handle);
          bounce(handle);
        };

        const softFocus = (handle) => {
          if (!markerByHandle.has(handle)) return;
          bounce(handle);
        };

        window.__DE_MAP = { update, focus, softFocus };

        // Initial pins (only if not in PRO-AM mode)
        if (!$proam?.checked) {
          const { shown } = getPageSlice(filteredCache);
          update(shown);
        }
      })
      .catch((err) => console.warn("[DE] Maps failed, list still works:", err));
  }

  // ---------- MAIN ----------
  async function main() {
    try {
      setStatus("Loading…");
      setCounts(0, 0);

      // 1) organizers
      const orgRes = await fetchPaged(organizersUrlBase, MAX_FETCH_PAGES);
      const orgItems = dedupeByHandle(orgRes.items || [], "organizers");

      organizers = orgItems
        .map((o) => ({
          handle: String(o.handle || "").trim(),
          name: String(o.name || "").trim(),
          logo: String(o.logo || "").trim(),
          pro_am: toBool(o.pro_am),
          website: String(o.website || "").trim(),
        }))
        .filter((o) => o.handle && o.name)
        .sort((a, b) => a.name.localeCompare(b.name));

      organizerByHandle = new Map(organizers.map((o) => [o.handle, o]));
      renderOrganizerMultiselect();
      wireOrganizerDropdown();
      populateSubmitOrganizerSelect();
      wireSubmitFormValidation();

      // 2) events
      const evRes = await fetchPaged(eventsUrlBase, MAX_FETCH_PAGES);
      const evItems = dedupeByHandle(evRes.items || [], "events");
      console.log("[DE] Events fetched (unique):", evItems.length);

      allEvents = evItems
        .map((e) => {
          const start = parseDate(e.start_date);
          const end = parseDate(e.end_date) || start;
          const ll = parseCoords(e.coords);

          const orgHandle = String(e.organizer_handle || "").trim();
          const org = orgHandle ? organizerByHandle.get(orgHandle) : null;

          return {
            handle: String(e.handle || "").trim(),
            name: String(e.name || ""),
            venue: String(e.venue || ""),
            address: String(e.address || ""),
            coords: String(e.coords || ""),
            start_date: String(e.start_date || ""),
            end_date: String(e.end_date || ""),
            tickets_url: String(e.tickets_url || ""),

            organizer_handle: orgHandle,
            organizer_name: String(e.organizer_name || org?.name || ""),
            organizer_logo: String(e.organizer_logo || org?.logo || ""),

            // robust PRO-AM flag:
            pro_am: toBool(e.pro_am) || toBool(org?.pro_am),

            lat: ll.lat,
            lng: ll.lng,

            _state: extractState(e.address),
            _startDate: start,
            _endDate: end,
          };
        })
        .filter((e) => e.handle);

      // Populate states
      if ($state) {
        const states = Array.from(new Set(allEvents.map((e) => e._state).filter(Boolean))).sort();
        $state.innerHTML =
          `<option value="">All states</option>` +
          states.map((st) => `<option value="${escapeHtml(st)}">${escapeHtml(st)}</option>`).join("");
      }

      wireControls();
      wireModal();
      await initBookmarks();

      pageIndex = 0;
      setStatus("");
      render();

      initMapNonBlocking();

      // Ensure debug panel shows immediately (if enabled)
      if (!$proam?.checked) deUpdateDebugPanel();
    } catch (err) {
      console.error("[DE] Fatal error:", err);
      setStatus("Events failed to load. Open console for details.");
      setCounts(0, 0);
      if ($pager) $pager.hidden = true;
      $cards.innerHTML = `<div class="de-empty">Events failed to load. Open console for details.</div>`;
    }
  }

  main();
})();
